import { CareerPath } from '@backrs_ui/common-ui';

import {
  CareerUserResponseSet,
  CareerQuestion,
  CareerUserResponseAnswer,
} from '@backrs_ui/grow-ui/src/models/models';

import { getQuestionByKey, getUserResponse } from '../scoreHelpers';

const CAREGIVER_FLEXIBILITY_QUESTION_KEY = 'caregiver_flexibility';

const CAREGIVER_FLEXIBILITY_ANSWER_KEYS = {
  NOT_IMPORTANT: 'caregiver_flexibility_not_important',
  SOMEWHAT_IMPORTANT: 'caregiver_flexibility_somewhat_important',
  IMPORTANT: 'caregiver_flexibility_important',
  VERY_IMPORTANT: 'caregiver_flexibility_very_important',
} as const;

type CaregiverFlexibilityOptions =
  | 'not_important'
  | 'somewhat_important'
  | 'important'
  | 'very_important';

const CAREGIVER_FLEXIBILITY_IMPORTANCE_MAP: Record<string, CaregiverFlexibilityOptions> = {
  [CAREGIVER_FLEXIBILITY_ANSWER_KEYS.NOT_IMPORTANT]: 'not_important',
  [CAREGIVER_FLEXIBILITY_ANSWER_KEYS.SOMEWHAT_IMPORTANT]: 'somewhat_important',
  [CAREGIVER_FLEXIBILITY_ANSWER_KEYS.IMPORTANT]: 'important',
  [CAREGIVER_FLEXIBILITY_ANSWER_KEYS.VERY_IMPORTANT]: 'very_important',
};

// Add the adjustment table
const FLEXIBILITY_ADJUSTMENT_TABLE = {
  not_important: { low: 0, medium: 0, high: 0 },
  somewhat_important: { low: -20, medium: 0, high: 20 },
  important: { low: -30, medium: -10, high: 30 },
  very_important: { low: -50, medium: -20, high: 50 },
} as const;

export function applyCaregiverFlexibilityAdjustment(
  baseScore: number,
  userResponseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
  careerPath: CareerPath,
): number {
  const context = getFlexibilityContext(userResponseSet, questions, careerPath);

  if (!context) {
    return baseScore; // No adjustment if context is not available
  }

  const { userImportance, careerPathCaregiverFlexibility } = context;

  if (!userImportance || !careerPathCaregiverFlexibility) {
    return baseScore; // No adjustment if either value is missing
  }

  const adjustmentPercentage =
    FLEXIBILITY_ADJUSTMENT_TABLE[userImportance][
      careerPathCaregiverFlexibility as keyof typeof FLEXIBILITY_ADJUSTMENT_TABLE.not_important
    ];

  const adjustedScore = baseScore + (baseScore * adjustmentPercentage) / 100;

  return Math.max(0, Math.min(100, adjustedScore)); // Ensure score stays within 0-100
}

function getUserCaregiverFlexibilityImportance(
  response: CareerUserResponseAnswer,
  question: CareerQuestion,
): CaregiverFlexibilityOptions | undefined {
  // Find the selected option
  const selectedOptionId = response.career_question_option_ids[0];
  const selectedOption = question.options.find((option) => option.id === selectedOptionId);

  if (!selectedOption) {
    return undefined;
  }

  // Map the calculator_key to importance level
  return CAREGIVER_FLEXIBILITY_IMPORTANCE_MAP[selectedOption.calculator_key];
}

export function shouldExcludeLowFlexibilityFromTop(
  careerPath: CareerPath,
  userResponseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
): boolean {
  const context = getFlexibilityContext(userResponseSet, questions, careerPath);

  if (!context) {
    return false; // No adjustment if context is not available
  }

  const { userImportance, careerPathCaregiverFlexibility } = context;

  if (!userImportance || !careerPathCaregiverFlexibility) {
    return false; // No adjustment if either value is missing
  }

  return (
    userImportance === 'very_important' && careerPathCaregiverFlexibility.toLowerCase() === 'low'
  );
}

type FlexibilityContext = Readonly<{
  userImportance: ReturnType<typeof getUserCaregiverFlexibilityImportance>;
  careerPathCaregiverFlexibility: string | undefined;
}>;

export function getFlexibilityContext(
  userResponseSet: CareerUserResponseSet,
  questions: CareerQuestion[],
  careerPath: CareerPath,
): FlexibilityContext | null {
  const caregiverFlexibilityQuestion = getQuestionByKey(
    questions,
    CAREGIVER_FLEXIBILITY_QUESTION_KEY,
  );

  if (!caregiverFlexibilityQuestion) {
    return null;
  }

  const flexibilityResponse = getUserResponse(userResponseSet, caregiverFlexibilityQuestion.id);

  if (!flexibilityResponse) {
    return null;
  }

  const userImportance = getUserCaregiverFlexibilityImportance(
    flexibilityResponse,
    caregiverFlexibilityQuestion,
  );

  if (!userImportance) {
    return null;
  }

  const careerPathCaregiverFlexibility =
    careerPath.caregiver_flexibility.caregiver_flexibility_score?.toLowerCase();

  if (!careerPathCaregiverFlexibility) {
    return null;
  }

  return {
    userImportance,
    careerPathCaregiverFlexibility,
  };
}
